import { styled as muiStyled } from '@mui/material/styles'
import Layout from '../../../layouts'
import Page from '@/components/Page'
import pageService, { GlobalSetting, Section, settingService, SeoMetadata } from '@/services/page'
import pathService from '@/services/path'
import type { GetStaticPaths, GetStaticProps } from 'next'
import { APPS } from '@/appConfig'
import type { ParsedUrlQuery } from 'querystring'
import { notEmpty } from '@/utils/common'
import { renderComponent } from '@/services/pageRender'
import styled from 'styled-components'

interface PathProps extends ParsedUrlQuery {
  appId: string
}

interface IndexProps {
  title?: string
  sections?: Section[]
  globalSetting?: GlobalSetting
  seoMetadataItemsCollection?: SeoMetadata[]
}

const RootStyle = styled('div')(() => ({
  height: '100%',
}))

const ContentStyle = muiStyled('div')(({ theme }) => ({
  overflow: 'hidden',
  position: 'relative',
}))

MintPage.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout variant="main">{page}</Layout>
}

export default function MintPage({
  sections,
  title,
  seoMetadataItemsCollection,
  globalSetting,
}: IndexProps) {
  const sectionBackgroundColors = ['transparent', 'transparent']
  const sectionComponents =
    sections
      ?.map((section, index) =>
        renderComponent(section, sectionBackgroundColors, index, globalSetting)
      )
      .filter(notEmpty) ?? []

  return (
    <Page title={title ?? ''} metadataItems={seoMetadataItemsCollection}>
      <RootStyle>
        <ContentStyle>{sectionComponents}</ContentStyle>
      </RootStyle>
    </Page>
  )
}

export const getStaticPaths: GetStaticPaths<PathProps> = async () => {
  const { getDefaultPaths } = pathService()
  const paths = APPS.flatMap((app) => getDefaultPaths(app.id))

  return {
    paths,
    fallback: true,
  }
}

export const getStaticProps: GetStaticProps<IndexProps, PathProps> = async ({ params, locale }) => {
  if (!params) throw new Error('No path parameters found')

  const { appId } = params

  const currentApp = APPS.find((app) => app.id === appId)
  const { getPage } = pageService(appId)
  const { getGlobalSetting } = settingService(appId)
  const [{ title, sections, seoMetadataItemsCollection }, globalSetting] = await Promise.all([
    getPage('/mint', { locale }),
    getGlobalSetting({ locale }),
  ])

  return {
    props: {
      title: title ?? globalSetting.seoMetadataItemsMap.title ?? currentApp?.title,
      sections,
      seoMetadataItemsCollection,
      globalSetting,
    },
    revalidate: 3600,
  }
}
